/**
 * The controls either side of the Grid: one key per Brush, in the Brushes' own
 * order, each flashing as the Walker strikes its Event, and the shared knobs. Plain DOM, built from the Brush table so the
 * order and the keys are stated in exactly one place; checked by eye.
 *
 * A knob is a dial with linear action: dragging up or right turns it up, down
 * or left turns it down, whatever the pointer's angle round it (knob.ts has
 * the arithmetic). Arrow keys and the wheel step it. It shows the hands' cyan
 * while a hand turns it here and when someone else's `ctl` moves it. The
 * Fuckery switch is a knob with three detents. Their hold rule is `slider.ts`.
 */

import type { Brush } from "./brush.ts"
import { BRUSHES } from "./brush.ts"
import { BRUSH_ICONS } from "./icons.ts"
import type { KnobRange } from "./knob.ts"
import { KNOB_TRAVEL_PX, sweep, turned } from "./knob.ts"

export interface BrushRail {
  /** Show this Brush as the chosen one. A click here shows itself. */
  readonly show: (brush: Brush) => void
  /** The Walker struck a Cell holding this value: its key flashes, fading over this many ms. */
  readonly strike: (value: number, fadeMs: number) => void
}

const span = (className: string, text?: string): HTMLSpanElement => {
  const element = document.createElement("span")
  element.className = className
  if (text !== undefined) element.textContent = text
  return element
}

const icon = (markup: string | undefined): HTMLSpanElement => {
  const element = span("icon")
  element.innerHTML = markup ?? ""
  return element
}

export const makeBrushRail = (element: HTMLElement, onPick: (brush: Brush) => void): BrushRail => {
  const buttons = BRUSHES.map((brush) => {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "key"
    button.title = `${brush.name} (${brush.key})`
    button.append(span("num", brush.key), icon(BRUSH_ICONS[brush.name]), span("name", brush.name))
    button.addEventListener("click", () => {
      show(brush)
      onPick(brush)
    })
    element.append(button)
    return { brush, button }
  })

  const show = (chosen: Brush) => {
    for (const { brush, button } of buttons) {
      button.setAttribute("aria-pressed", String(brush === chosen))
    }
  }

  const strike = (value: number, fadeMs: number) => {
    const struck = buttons.find(({ brush }) => brush.value === value)?.button
    if (struck === undefined) return
    struck.style.setProperty("--fade", `${Math.round(fadeMs)}ms`)
    struck.classList.remove("struck")
    // Read layout so removing and adding the class restarts the flash.
    void struck.offsetWidth
    struck.classList.add("struck")
  }

  return { show, strike }
}

export interface SliderSpec extends KnobRange {
  readonly name: string
  /** The readout: whole bpm, or Temperature in 0.01 steps. */
  readonly format: (value: number) => string
}

export interface RailSlider {
  /** Show this value on the knob and its readout. */
  readonly show: (value: number) => void
  /** Flash briefly in the hands' cyan: the room changed it. */
  readonly flash: () => void
  /** A knob is the room's, so it moves only once this page has joined. */
  readonly enable: () => void
}

const SVG = "http://www.w3.org/2000/svg"
const svgElement = (name: string, attributes: Record<string, string | number>): SVGElement => {
  const element = document.createElementNS(SVG, name)
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, String(value))
  return element
}

/** The dial sweeps 270°, from lower left round the top to lower right. */
const SWEEP_DEG = 270
const polar = (radius: number, degrees: number): string => {
  const radians = (degrees * Math.PI) / 180
  return `${(32 + radius * Math.sin(radians)).toFixed(2)} ${(32 - radius * Math.cos(radians)).toFixed(2)}`
}
const ARC = `M ${polar(27, -SWEEP_DEG / 2)} A 27 27 0 1 1 ${polar(27, SWEEP_DEG / 2)}`

interface Dial {
  readonly root: HTMLElement
  readonly readout: HTMLElement
  readonly show: (value: number) => void
  readonly flash: () => void
  readonly enable: () => void
}

/**
 * One dial: its name, the knob, and its readout. `onTurn` fires with every new
 * value the hand reaches, and `onRelease` when it lets go.
 */
const makeDial = (
  element: HTMLElement,
  name: string,
  range: KnobRange,
  handlers: { readonly onTurn: (value: number) => void; readonly onRelease: () => void },
  detents = 0
): Dial => {
  const root = document.createElement("div")
  root.className = "knob"
  root.setAttribute("role", "slider")
  root.setAttribute("aria-label", name)
  root.setAttribute("aria-valuemin", String(range.min))
  root.setAttribute("aria-valuemax", String(range.max))
  root.setAttribute("aria-disabled", "true")
  root.tabIndex = -1

  const dial = svgElement("svg", { viewBox: "0 0 64 64", class: "dial", "aria-hidden": "true" })
  dial.append(svgElement("path", { d: ARC, class: "track", pathLength: 100 }))
  const arc = svgElement("path", { d: ARC, class: "arc", pathLength: 100 })
  dial.append(arc)
  for (let i = 0; i < detents; i++) {
    const degrees = -SWEEP_DEG / 2 + (SWEEP_DEG * i) / (detents - 1)
    dial.append(svgElement("path", { d: `M ${polar(31, degrees)} L ${polar(32.5, degrees)}`, class: "tick" }))
  }
  dial.append(svgElement("circle", { cx: 32, cy: 32, r: 19, class: "cap" }))
  const pointer = svgElement("g", { class: "pointer" })
  pointer.append(svgElement("path", { d: "M32 16 V24" }))
  dial.append(pointer)

  const readout = span("readout")
  root.append(span("label", name), dial, readout)
  element.append(root)

  let value = range.min
  let enabled = false
  let grab: { readonly x: number; readonly y: number; readonly from: number } | undefined

  const turnTo = (next: number) => {
    if (next === value) return
    handlers.onTurn(next)
  }

  root.addEventListener("pointerdown", (event) => {
    if (!enabled) return
    event.preventDefault()
    root.focus()
    root.setPointerCapture(event.pointerId)
    grab = { x: event.clientX, y: event.clientY, from: value }
    root.classList.add("turning")
  })
  root.addEventListener("pointermove", (event) => {
    if (grab === undefined) return
    turnTo(turned(range, grab.from, grab.y - event.clientY + (event.clientX - grab.x)))
  })
  const letGo = () => {
    if (grab === undefined) return
    grab = undefined
    root.classList.remove("turning")
    handlers.onRelease()
  }
  root.addEventListener("pointerup", letGo)
  root.addEventListener("pointercancel", letGo)
  root.addEventListener("lostpointercapture", letGo)
  root.addEventListener("keydown", (event) => {
    if (!enabled) return
    const big = (range.max - range.min) / 10
    const by: Record<string, number> = {
      ArrowUp: range.step,
      ArrowRight: range.step,
      ArrowDown: -range.step,
      ArrowLeft: -range.step,
      PageUp: big,
      PageDown: -big,
      Home: -Infinity,
      End: Infinity
    }
    const delta = by[event.key]
    if (delta === undefined) return
    event.preventDefault()
    const next = Number.isFinite(delta)
      ? turned(range, value, (delta / (range.max - range.min)) * KNOB_TRAVEL_PX)
      : delta > 0
        ? range.max
        : range.min
    turnTo(next)
    handlers.onRelease()
  })
  root.addEventListener(
    "wheel",
    (event) => {
      if (!enabled) return
      event.preventDefault()
      turnTo(
        turned(
          range,
          value,
          ((event.deltaY < 0 ? 1 : -1) * range.step * KNOB_TRAVEL_PX) / (range.max - range.min)
        )
      )
      handlers.onRelease()
    },
    { passive: false }
  )

  return {
    root,
    readout,
    show: (next) => {
      value = next
      const fraction = sweep(range, next)
      arc.setAttribute("stroke-dasharray", `${(fraction * 100).toFixed(2)} 100`)
      pointer.setAttribute("transform", `rotate(${(-SWEEP_DEG / 2 + SWEEP_DEG * fraction).toFixed(1)} 32 32)`)
      root.setAttribute("aria-valuenow", String(next))
    },
    flash: () => {
      root.classList.remove("flash")
      // Read layout so removing and adding the class restarts the animation.
      void root.offsetWidth
      root.classList.add("flash")
    },
    enable: () => {
      enabled = true
      root.tabIndex = 0
      root.setAttribute("aria-disabled", "false")
    }
  }
}

/**
 * One shared knob. `onDrag` fires on every movement, keyboard and wheel
 * included, and `onRelease` when the hand lets go. What it shows is the page's
 * to decide, through `show`.
 */
export const makeSlider = (
  element: HTMLElement,
  spec: SliderSpec,
  handlers: { readonly onDrag: (value: number) => void; readonly onRelease: () => void }
): RailSlider => {
  const dial = makeDial(element, spec.name, spec, { onTurn: handlers.onDrag, onRelease: handlers.onRelease })
  return {
    show: (value) => {
      dial.show(value)
      dial.readout.textContent = spec.format(value)
      dial.root.setAttribute("aria-valuetext", spec.format(value))
    },
    flash: dial.flash,
    enable: dial.enable
  }
}

/**
 * One shared switch: a knob that clicks between positions, its readout a pip
 * per position lit up to the chosen one, like a meter. `onPick` fires with the
 * position's index as the knob reaches it; what it shows is the page's to
 * decide, through `show`, like a slider's.
 */
export const makeSwitch = (
  element: HTMLElement,
  spec: { readonly name: string; readonly positions: ReadonlyArray<string> },
  onPick: (index: number) => void
): RailSlider => {
  const range = { min: 0, max: spec.positions.length - 1, step: 1 }
  const dial = makeDial(
    element,
    spec.name,
    range,
    { onTurn: onPick, onRelease: () => {} },
    spec.positions.length
  )
  dial.root.classList.add("switch")
  const pips = spec.positions.map(() => span("pip"))
  dial.readout.append(...pips)
  return {
    show: (index) => {
      dial.show(index)
      pips.forEach((pip, at) => pip.classList.toggle("lit", at <= index))
      dial.root.setAttribute("aria-valuetext", spec.positions[index] ?? "")
      dial.root.title = spec.positions[index] ?? ""
    },
    flash: dial.flash,
    enable: dial.enable
  }
}
